import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Descriptions,
  Dropdown,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  EditOutlined,
  ReloadOutlined,
  ExportOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  LinkOutlined,
  MoreOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import {
  TaxDocumentRecord,
  useGetPropertyFoldersQuery,
  useUpdateTaxDocumentInfoMutation,
} from '@/store/apis/taxDocumentsApi';
import {
  DOC_TYPE_LABELS,
  SUB_TYPE_LABELS,
  TaxDocumentType,
  TaxSupportingDocsSubType,
} from '../constants/tax-documents.enum';
import { DaysAgo, DaysAgoDetail } from '@/components/Shared';
import { assignablePropertyOptions } from '../../TaxDeals/utils/suggested-property.utils';
import DataViewer from './DataViewer';
import TaxRightPanel from './TaxRightPanel';

const { Text } = Typography;

const DOC_TYPE_ORDER: TaxDocumentType[] = [
  TaxDocumentType.SUPPORTING_DOCS,
  TaxDocumentType.TAX_RETURN,
  TaxDocumentType.SIGNED_FORMS,
  TaxDocumentType.NOA,
  TaxDocumentType.OTHER_ATO_DOCS,
  TaxDocumentType.DEPRECIATION_SCHEDULE,
  TaxDocumentType.SIGNED_AGREEMENTS,
  TaxDocumentType.OTHER_DOCS,
  TaxDocumentType.UNKNOWN,
];

const SUB_TYPE_ORDER: TaxSupportingDocsSubType[] = Object.values(
  TaxSupportingDocsSubType,
);

const STATUS_BADGE: Record<
  string,
  'success' | 'error' | 'processing' | 'default'
> = {
  SUCCEEDED: 'success',
  FAILED: 'error',
  PENDING: 'processing',
};

const DOC_TYPE_OPTIONS = Object.entries(DOC_TYPE_LABELS).map(([v, l]) => ({
  value: v,
  label: l,
}));

const SUB_TYPE_OPTIONS = Object.entries(SUB_TYPE_LABELS).map(([v, l]) => ({
  value: v,
  label: l,
}));

// Mirrors the backend's getYearSubFolderNameForSubType — only these sub-types
// can be filed under a property.
const NO_PROPERTY_SUB_TYPES = new Set<TaxSupportingDocsSubType>([
  TaxSupportingDocsSubType.ATO_DOCUMENTS,
  TaxSupportingDocsSubType.CGT_RELATED_ITEMS,
  TaxSupportingDocsSubType.INDIVIDUAL_SHARES,
  TaxSupportingDocsSubType.OTHERS,
  TaxSupportingDocsSubType.UNKNOWN,
]);

const canAssignProperty = (
  docType: TaxDocumentType,
  subType?: TaxSupportingDocsSubType,
) =>
  docType === TaxDocumentType.SUPPORTING_DOCS &&
  !!subType &&
  !NO_PROPERTY_SUB_TYPES.has(subType);

const splitFilename = (filename: string) => {
  const dotIdx = filename.lastIndexOf('.');
  if (dotIdx <= 0) return { stem: filename, ext: '' };
  return { stem: filename.slice(0, dotIdx), ext: filename.slice(dotIdx) };
};

const getDocumentDisplayName = (doc: TaxDocumentRecord) =>
  doc.newName ?? doc.drive?.name ?? '';

const sortDocsByName = (docs: TaxDocumentRecord[]) =>
  [...docs].sort((a, b) =>
    getDocumentDisplayName(a).localeCompare(
      getDocumentDisplayName(b),
      undefined,
      {
        sensitivity: 'base',
        numeric: true,
      },
    ),
  );

interface FileNodeProps {
  doc: TaxDocumentRecord;
  dealId?: string;
  indent: number;
  isSelected?: boolean;
  onSelect?: (doc: TaxDocumentRecord) => void;
  /** Whether edit affordances (rename/reclassify) are available. Default true. */
  canEdit?: boolean;
}

const FileNode: React.FC<FileNodeProps> = ({
  doc,
  dealId,
  indent,
  isSelected,
  onSelect,
  canEdit = true,
}) => {
  const { token } = theme.useToken();
  const [expanded, setExpanded] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [editing, setEditing] = useState(false);

  const name = doc.copied?.newName ?? doc.drive?.name ?? '—';
  const propertyName = doc.property?.displayName ?? doc.extras?.property;
  const { stem: origStem, ext } = useMemo(() => splitFilename(name), [name]);

  const [nameStem, setNameStem] = useState(origStem);
  const [editDocType, setEditDocType] = useState<TaxDocumentType>(doc.docType);
  const [editSubType, setEditSubType] = useState<
    TaxSupportingDocsSubType | undefined
  >(doc.docSubType);
  const [editFY, setEditFY] = useState<number | undefined>(
    doc.financialYearEnd ?? undefined,
  );
  const [editProperty, setEditProperty] = useState<string>(
    doc.property?.displayName ?? '',
  );

  const [updateDoc, { isLoading: saving }] = useUpdateTaxDocumentInfoMutation();

  const showProperty = canAssignProperty(editDocType, editSubType);
  const { data: propertyFolders } = useGetPropertyFoldersQuery(dealId ?? '', {
    skip: !editing || !dealId,
  });
  const propertyOptions = useMemo(
    () => assignablePropertyOptions(propertyFolders),
    [propertyFolders],
  );

  // A document already filed under something outside the assignable list (an
  // auto-created folder, or a property since unstarred) must stay selectable,
  // or opening the editor would silently clear its assignment on save.
  const propertySelectOptions = useMemo(() => {
    const current = editProperty?.trim();
    if (!current || propertyOptions.some((o) => o.value === current)) {
      return propertyOptions;
    }
    return [
      { value: current, label: `${current} (not linked)` },
      ...propertyOptions,
    ];
  }, [propertyOptions, editProperty]);

  // Keep edit fields in sync with doc when not actively editing
  useEffect(() => {
    if (editing) return;
    const { stem } = splitFilename(
      doc.copied?.newName ?? doc.drive?.name ?? '',
    );
    setNameStem(stem);
    setEditDocType(doc.docType);
    setEditSubType(doc.docSubType);
    setEditFY(doc.financialYearEnd ?? undefined);
    setEditProperty(doc.property?.displayName ?? '');
  }, [
    doc.copied?.newName,
    doc.drive?.name,
    doc.docType,
    doc.docSubType,
    doc.financialYearEnd,
    doc.property?.displayName,
    editing,
  ]);

  const hasData = Boolean(doc.data || doc.extras);
  const hasMeta = Boolean(
    doc.financialYearEnd ||
    doc.startDate ||
    doc.issueDate ||
    doc.identifiers?.bank_name ||
    doc.identifiers?.account_last4,
  );
  const hasContent = hasData || hasMeta || Boolean(doc.status);
  const canPreview = Boolean(doc.blob?.container && doc.blob?.blobName);
  const selectMode = Boolean(onSelect);

  const save = () => {
    const trimmed = nameStem.trim();
    if (!trimmed) return;
    const nameChanged = trimmed !== origStem;
    const trimmedProperty = editProperty.trim();
    const propertyChanged =
      canAssignProperty(editDocType, editSubType) &&
      trimmedProperty !== (doc.property?.displayName ?? '');
    setEditing(false);
    updateDoc({
      documentId: doc.id,
      taxDealId: dealId,
      ...(nameChanged ? { newName: trimmed + ext } : {}),
      ...(propertyChanged ? { property: trimmedProperty || null } : {}),
      docType: editDocType,
      subType: editSubType,
      financialYearEnd: editFY,
    });
  };

  const openEdit = () => {
    const { stem } = splitFilename(
      doc.copied?.newName ?? doc.drive?.name ?? '',
    );
    setNameStem(stem);
    setEditDocType(doc.docType);
    setEditSubType(doc.docSubType);
    setEditFY(doc.financialYearEnd ?? undefined);
    setEditProperty(doc.property?.displayName ?? '');
    setEditing(true);
  };

  const cancel = () => {
    setNameStem(origStem);
    setEditDocType(doc.docType);
    setEditSubType(doc.docSubType);
    setEditFY(doc.financialYearEnd ?? undefined);
    setEditProperty(doc.property?.displayName ?? '');
    setEditing(false);
  };

  const handleCaretClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasContent) return;
    const next = !expanded;
    setExpanded(next);
    if (!next) setPreviewing(false);
  };

  const handleRowClick = () => {
    if (selectMode) {
      onSelect!(doc);
    } else {
      if (!hasContent) return;
      const next = !expanded;
      setExpanded(next);
      if (!next) setPreviewing(false);
    }
  };

  const openViewer = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!dealId) return;
    const url = `/tax-documents/${dealId}?docId=${doc.id}`;
    const w = 1300;
    const h = 840;
    window.open(
      url,
      '_blank',
      `width=${w},height=${h},left=${(screen.width - w) / 2},top=${(screen.height - h) / 2},resizable=yes,scrollbars=yes`,
    );
  };

  const driveLink = doc.drive?.webViewLink;
  const docFolderId = doc.copied?.destFolderId;
  const docFolderUrl = `https://drive.google.com/drive/u/0/folders/${docFolderId}`;
  const editMenuItems: MenuProps['items'] = [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Rename / Edit',
      onClick: openEdit,
    },
    ...(driveLink
      ? [
          {
            key: 'drive',
            icon: <LinkOutlined />,
            label: 'Open in Drive',
            onClick: () => {
              window.open(driveLink, '_blank', 'noopener,noreferrer');
            },
          },
        ]
      : []),
    ...(docFolderId
      ? [
          {
            key: 'driveFolder',
            icon: <LinkOutlined />,
            label: 'Open Drive Folder',
            onClick: () => {
              window.open(docFolderUrl, '_blank', 'noopener,noreferrer');
            },
          },
        ]
      : []),
  ];

  const rowBg = isSelected
    ? token.colorPrimaryBg
    : expanded
      ? token.colorFillAlter
      : 'transparent';

  const fileRowContent = (
    <div
      onClick={handleRowClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: `5px 8px 5px ${indent}px`,
        cursor: hasContent || selectMode ? 'pointer' : 'default',
        borderRadius: 4,
        backgroundColor: rowBg,
        borderLeft: isSelected
          ? `2px solid ${token.colorPrimary}`
          : '2px solid transparent',
        transition: 'background 0.1s',
      }}
    >
      {/* Caret: toggles OCR expansion in both modes */}
      <span onClick={handleCaretClick} style={{ flexShrink: 0, lineHeight: 1 }}>
        {hasContent ? (
          expanded ? (
            <CaretDownOutlined
              style={{ fontSize: 9, color: token.colorTextQuaternary }}
            />
          ) : (
            <CaretRightOutlined
              style={{ fontSize: 9, color: token.colorTextQuaternary }}
            />
          )
        ) : (
          <span style={{ display: 'inline-block', width: 9 }} />
        )}
      </span>

      <FileTextOutlined
        style={{
          color: isSelected ? token.colorPrimary : token.colorTextSecondary,
          flexShrink: 0,
          fontSize: 13,
        }}
      />

      <Text
        ellipsis={{ tooltip: name }}
        style={{
          fontSize: 13,
          flex: 1,
          minWidth: 0,
          color: isSelected ? token.colorPrimary : undefined,
        }}
      >
        {name}
      </Text>
      {!selectMode && propertyName && (
        <Tooltip title={propertyName}>
          <Tag
            color="geekblue"
            style={{ fontSize: 11, marginRight: 8, maxWidth: 140 }}
          >
            <span
              style={{
                display: 'inline-block',
                maxWidth: 128,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'middle',
              }}
            >
              {propertyName}
            </span>
          </Tag>
        </Tooltip>
      )}

      {doc.createdAt && (
        <DaysAgo
          showWeeks={true}
          detailLevel={DaysAgoDetail.COMPACT}
          date={doc.createdAt}
          style={{ marginRight: 0 }}
        />
      )}

      {/* Status badge — inline mode only; shown in OCR section in select mode */}
      {!selectMode && doc.status && (
        <Badge
          status={STATUS_BADGE[doc.status] ?? 'default'}
          text={
            <Text
              style={{
                fontSize: 11,
                color: token.colorTextSecondary,
                marginRight: 8,
              }}
            >
              {doc.status}
            </Text>
          }
        />
      )}

      {doc.financialYearEnd && (
        <Tag style={{ fontSize: 11, marginRight: 8 }}>
          FY{doc.financialYearEnd}
        </Tag>
      )}

      {/* Open Viewer button — inline mode only */}
      {!selectMode && dealId && (
        <Tooltip title="Open in full viewer">
          <Button
            size="small"
            type="text"
            icon={<ExportOutlined style={{ fontSize: 11 }} />}
            onClick={openViewer}
            style={{
              fontSize: 12,
              padding: '0 6px',
              height: 22,
              flexShrink: 0,
            }}
          >
            Open
          </Button>
        </Tooltip>
      )}

      {/* Open in Drive button — shown when a Drive link is available */}
      {!selectMode && docFolderId && (
        <Tooltip title="Open Google Drive Folder">
          <Button
            size="small"
            type="text"
            icon={<LinkOutlined style={{ fontSize: 11 }} />}
            onClick={(e) => {
              e.stopPropagation();
              window.open(docFolderUrl, '_blank', 'noopener,noreferrer');
            }}
            style={{
              fontSize: 12,
              padding: '0 6px',
              height: 22,
              flexShrink: 0,
            }}
          >
            Drive
          </Button>
        </Tooltip>
      )}

      {/* Three-dots menu — select mode only */}
      {selectMode && (
        <Dropdown
          menu={{ items: editMenuItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            size="small"
            type="text"
            icon={<MoreOutlined style={{ fontSize: 14 }} />}
            onClick={(e) => e.stopPropagation()}
            style={{ padding: '0 4px', height: 22, flexShrink: 0 }}
          />
        </Dropdown>
      )}
    </div>
  );

  return (
    <div>
      {/* Right-click context menu — available in any mode when editing is allowed. */}
      {canEdit ? (
        <Dropdown menu={{ items: editMenuItems }} trigger={['contextMenu']}>
          {fileRowContent}
        </Dropdown>
      ) : (
        fileRowContent
      )}

      {/* Inline edit form — rendered in any mode when an edit is open. */}
      {canEdit && editing && (
        <div
          style={{
            margin: `4px 8px 6px ${indent + 22}px`,
            padding: '12px 14px',
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: 6,
            backgroundColor: token.colorFillAlter,
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            {/* File name */}
            <div>
              <Text
                type="secondary"
                style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
              >
                File Name
              </Text>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  size="small"
                  value={nameStem}
                  onChange={(e) => setNameStem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') save();
                    if (e.key === 'Escape') cancel();
                  }}
                  style={{ fontSize: 13 }}
                />
                <Input
                  size="small"
                  value={ext || '(no ext)'}
                  disabled
                  style={{
                    width: ext ? 52 : 72,
                    fontSize: 13,
                    color: token.colorTextTertiary,
                    textAlign: 'center',
                    flexShrink: 0,
                  }}
                />
              </Space.Compact>
            </div>

            {/* Doc Type */}
            <div>
              <Text
                type="secondary"
                style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
              >
                Doc Type
              </Text>
              <Select
                size="small"
                value={editDocType}
                options={DOC_TYPE_OPTIONS}
                onChange={(v) => setEditDocType(v as TaxDocumentType)}
                style={{ width: '100%' }}
              />
            </div>

            {/* Sub Type */}
            <div>
              <Text
                type="secondary"
                style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
              >
                Sub Type
              </Text>
              <Select
                size="small"
                value={editSubType}
                options={SUB_TYPE_OPTIONS}
                onChange={(v) =>
                  setEditSubType(v as TaxSupportingDocsSubType | undefined)
                }
                allowClear
                placeholder="None"
                style={{ width: '100%' }}
              />
            </div>

            {/* Property */}
            {showProperty && (
              <div>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
                >
                  Property
                </Text>
                {/* Select, not AutoComplete: typing a free-text address here
                    created a new folder, which is how duplicate properties got
                    entrenched. Only linked properties can be filed under. */}
                {/* No showSearch: a deal has a handful of properties, so the
                    full list is visible at once and a search box only adds a
                    step. */}
                <Select
                  size="small"
                  value={editProperty || undefined}
                  onChange={(v?: string) => setEditProperty(v ?? '')}
                  options={propertySelectOptions}
                  allowClear
                  onClear={() => setEditProperty('')}
                  placeholder="None — pick a linked property"
                  notFoundContent={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      No linked properties on this deal
                    </Text>
                  }
                  style={{ width: '100%' }}
                />
              </div>
            )}

            {/* FY End */}
            <div>
              <Text
                type="secondary"
                style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
              >
                FY End
              </Text>
              <InputNumber
                size="small"
                value={editFY}
                onChange={(v) => setEditFY(v ?? undefined)}
                placeholder="e.g. 2024"
                min={1990}
                max={2099}
                style={{ width: '100%' }}
              />
            </div>

            {/* Actions */}
            <Space size={6}>
              <Button
                size="small"
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={save}
              >
                Save
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                disabled={saving}
                onClick={cancel}
              >
                Cancel
              </Button>
            </Space>
          </Space>
        </div>
      )}

      {/* Expanded: metadata + extracted data */}
      {expanded && hasContent && (
        <div
          style={{
            marginLeft: indent + 22,
            marginRight: 8,
            padding: '10px 12px',
            marginBottom: 4,
            borderLeft: `2px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorBgContainer,
            borderRadius: '0 4px 4px 0',
          }}
        >
          {(hasMeta || (selectMode && doc.status)) && (
            <Space
              direction="vertical"
              size={6}
              style={{ width: '100%', marginBottom: hasData ? 10 : 0 }}
            >
              {/* Row 1 — FileInvite Name */}
              {doc.drive?.name && (
                <div style={{ display: 'flex', gap: 8, minWidth: 0 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, flexShrink: 0 }}
                  >
                    FileInvite Name:
                  </Text>
                  <Text
                    strong
                    ellipsis={{ tooltip: doc.drive.name }}
                    style={{ fontSize: 12, minWidth: 0 }}
                  >
                    {doc.drive.name}
                  </Text>
                </div>
              )}

              {/* Row 2 — Period */}
              {(doc.startDate || doc.issueDate) && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, flexShrink: 0 }}
                  >
                    {doc.startDate ? 'Period:' : 'Issue Date:'}
                  </Text>
                  <Text strong style={{ fontSize: 12 }}>
                    {doc.startDate
                      ? `${doc.startDate} → ${doc.endDate}`
                      : doc.issueDate}
                  </Text>
                </div>
              )}

              {/* Row 3 — chip row: status, FY, bank, account, issued */}
              <Space size={[12, 4]} wrap>
                {selectMode && doc.status && (
                  <Badge
                    status={STATUS_BADGE[doc.status] ?? 'default'}
                    text={<Text style={{ fontSize: 11 }}>{doc.status}</Text>}
                  />
                )}
                {doc.financialYearEnd && (
                  <Tag style={{ fontSize: 11, margin: 0 }}>
                    FY{doc.financialYearEnd}
                  </Tag>
                )}
                {doc.identifiers?.bank_name && (
                  <Text style={{ fontSize: 11 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Bank:{' '}
                    </Text>
                    <Text strong style={{ fontSize: 11 }}>
                      {doc.identifiers.bank_name}
                    </Text>
                  </Text>
                )}
                {doc.identifiers?.account_last4 && (
                  <Text style={{ fontSize: 11 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Acct:{' '}
                    </Text>
                    <Text strong style={{ fontSize: 11 }}>
                      ···{doc.identifiers.account_last4}
                    </Text>
                  </Text>
                )}
                {doc.issueDate && doc.startDate && (
                  <Text style={{ fontSize: 11 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Issued:{' '}
                    </Text>
                    <Text strong style={{ fontSize: 11 }}>
                      {doc.issueDate}
                    </Text>
                  </Text>
                )}
              </Space>
            </Space>
          )}
          {hasData && <DataViewer data={doc.data} extras={doc.extras} />}

          {/* Preview — inline mode only */}
          {!selectMode && canPreview && (
            <div style={{ marginTop: 10 }}>
              <Button
                size="small"
                type="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewing(true);
                }}
              >
                Preview Document
              </Button>
            </div>
          )}
        </div>
      )}

      {/*
        Mounted only while open. A drawer per file node would put one behind
        every row in a tree that routinely runs to hundreds, and the close is
        the drawer's own - it no longer depends on the row staying expanded.
      */}
      {!selectMode && previewing && (
        <TaxRightPanel
          document={doc}
          open
          onClose={() => setPreviewing(false)}
        />
      )}
    </div>
  );
};

// ─── Sub-type Folder ─────────────────────────────────────────────────────────

interface SubTypeFolderProps {
  subType: TaxSupportingDocsSubType;
  docs: TaxDocumentRecord[];
  dealId?: string;
  indent: number;
  selectedDocId?: string;
  onSelectDoc?: (doc: TaxDocumentRecord) => void;
  canEdit?: boolean;
}

const SubTypeFolder: React.FC<SubTypeFolderProps> = ({
  subType,
  docs,
  dealId,
  indent,
  selectedDocId,
  onSelectDoc,
  canEdit,
}) => {
  const { token } = theme.useToken();
  const [open, setOpen] = useState(true);
  const sortedDocs = useMemo(() => sortDocsByName(docs), [docs]);

  return (
    <div style={{ marginBottom: 2 }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: `4px 8px 4px ${indent}px`,
          cursor: 'pointer',
          borderRadius: 4,
          userSelect: 'none',
        }}
      >
        {open ? (
          <FolderOpenOutlined
            style={{ color: '#faad14', fontSize: 14, flexShrink: 0 }}
          />
        ) : (
          <FolderOutlined
            style={{ color: '#faad14', fontSize: 14, flexShrink: 0 }}
          />
        )}
        <Text style={{ fontSize: 12, color: token.colorText }}>
          {SUB_TYPE_LABELS[subType] ?? subType}
        </Text>
        <Badge
          count={docs.length}
          overflowCount={999}
          style={{
            backgroundColor: token.colorFillSecondary,
            color: token.colorTextSecondary,
            boxShadow: 'none',
            fontSize: 10,
          }}
        />
      </div>

      {open &&
        sortedDocs.map((doc) => (
          <FileNode
            key={doc.id}
            doc={doc}
            dealId={dealId}
            indent={indent + 16}
            isSelected={selectedDocId === doc.id}
            onSelect={onSelectDoc}
            canEdit={canEdit}
          />
        ))}
    </div>
  );
};

// ─── Doc Type Folder ─────────────────────────────────────────────────────────

interface DocTypeFolderProps {
  type: TaxDocumentType;
  docs: TaxDocumentRecord[];
  dealId?: string;
  selectedDocId?: string;
  onSelectDoc?: (doc: TaxDocumentRecord) => void;
  canEdit?: boolean;
}

const DocTypeFolder: React.FC<DocTypeFolderProps> = ({
  type,
  docs,
  dealId,
  selectedDocId,
  onSelectDoc,
  canEdit,
}) => {
  const { token } = theme.useToken();
  const [open, setOpen] = useState(true);
  const sortedDocs = useMemo(() => sortDocsByName(docs), [docs]);

  const isSupporting = type === TaxDocumentType.SUPPORTING_DOCS;

  const subGroups = isSupporting
    ? docs.reduce<
        Partial<Record<TaxSupportingDocsSubType, TaxDocumentRecord[]>>
      >((acc, doc) => {
        const sub = doc.docSubType ?? TaxSupportingDocsSubType.UNKNOWN;
        if (!acc[sub]) acc[sub] = [];
        acc[sub]!.push(doc);
        return acc;
      }, {})
    : {};

  const sortedSubTypes = isSupporting
    ? (Object.keys(subGroups) as TaxSupportingDocsSubType[]).sort(
        (a, b) => SUB_TYPE_ORDER.indexOf(a) - SUB_TYPE_ORDER.indexOf(b),
      )
    : [];

  return (
    <div
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 6,
        marginBottom: 8,
        overflow: 'hidden',
        backgroundColor: token.colorBgContainer,
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: open ? token.colorFillAlter : token.colorBgContainer,
          borderBottom: open
            ? `1px solid ${token.colorBorderSecondary}`
            : 'none',
          userSelect: 'none',
        }}
      >
        {open ? (
          <FolderOpenOutlined
            style={{ color: token.colorPrimary, fontSize: 15, flexShrink: 0 }}
          />
        ) : (
          <FolderOutlined
            style={{ color: token.colorPrimary, fontSize: 15, flexShrink: 0 }}
          />
        )}
        <Text strong style={{ fontSize: 13 }}>
          {DOC_TYPE_LABELS[type] ?? type}
        </Text>
        <Badge
          count={docs.length}
          overflowCount={999}
          style={{
            backgroundColor: token.colorPrimaryBg,
            color: token.colorPrimary,
            boxShadow: 'none',
            fontSize: 11,
          }}
        />
        <Space style={{ flex: 1, justifyContent: 'flex-end' }}>
          {open ? (
            <CaretDownOutlined
              style={{ fontSize: 11, color: token.colorTextTertiary }}
            />
          ) : (
            <CaretRightOutlined
              style={{ fontSize: 11, color: token.colorTextTertiary }}
            />
          )}
        </Space>
      </div>

      {open && (
        <div style={{ padding: '6px 8px 8px' }}>
          {isSupporting
            ? sortedSubTypes.map((sub) => (
                <SubTypeFolder
                  key={sub}
                  subType={sub}
                  docs={subGroups[sub]!}
                  dealId={dealId}
                  indent={8}
                  selectedDocId={selectedDocId}
                  onSelectDoc={onSelectDoc}
                  canEdit={canEdit}
                />
              ))
            : sortedDocs.map((doc) => (
                <FileNode
                  key={doc.id}
                  doc={doc}
                  dealId={dealId}
                  indent={8}
                  isSelected={selectedDocId === doc.id}
                  onSelect={onSelectDoc}
                  canEdit={canEdit}
                />
              ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Export ─────────────────────────────────────────────────────────────

export interface TaxDocumentExplorerProps {
  docs: TaxDocumentRecord[];
  dealId?: string;
  selectedDocId?: string;
  onSelectDoc?: (doc: TaxDocumentRecord) => void;
  /** Enable rename/reclassify affordances. Defaults to true. */
  canEdit?: boolean;
}

const TaxDocumentExplorer: React.FC<TaxDocumentExplorerProps> = ({
  docs,
  dealId,
  selectedDocId,
  onSelectDoc,
  canEdit = true,
}) => {
  if (!docs.length) {
    return (
      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
        No documents match the selected filters.
      </Text>
    );
  }

  const grouped = docs.reduce<
    Partial<Record<TaxDocumentType, TaxDocumentRecord[]>>
  >((acc, doc) => {
    const t = doc.docType ?? TaxDocumentType.UNKNOWN;
    if (!acc[t]) acc[t] = [];
    acc[t]!.push(doc);
    return acc;
  }, {});

  const presentTypes = DOC_TYPE_ORDER.filter(
    (t) => (grouped[t]?.length ?? 0) > 0,
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {docs.length} document{docs.length !== 1 ? 's' : ''}
        </Text>
        <Button
          size="small"
          type="text"
          icon={<ReloadOutlined />}
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      </div>
      {presentTypes.map((type) => (
        <DocTypeFolder
          key={type}
          type={type}
          docs={grouped[type]!}
          dealId={dealId}
          selectedDocId={selectedDocId}
          onSelectDoc={onSelectDoc}
          canEdit={canEdit}
        />
      ))}
    </div>
  );
};

export default TaxDocumentExplorer;
